import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";
import {
  useEnterTodayRaceMutation,
  useGetEmojiPenaltyQuery,
  useGetInfoQuery,
  useGetStatusQuery,
  useGetTodayImageQuery,
  useSetQuoteMutation,
  useSuggestQuoteMutation,
  useTriggerWaybackMutation,
} from "../api/artOfTheDayApi";
import type {
  ArtInfo,
  EmojiPenaltyResponse,
  StatusResponse,
  TodayResponse,
  WaitingRoomRacer,
} from "../api/types";
import type { RaceTapPath } from "../api/artOfTheDayApi";
import { reduceTodayResponse } from "../state/aotdStateMachine";
import type { UiState } from "../state/types";
import { useAotdRaceSocket } from "./useAotdRaceSocket";
import {
  noteServerTime,
  raceClockAheadMs,
  raceServerNow,
  useRaceClock,
  type RaceClock,
} from "./useRaceClock";
import {
  burstWaitMs,
  inArtHoldWindow,
  requestCouldNotEnter as couldNotEnter,
  type SentRequest,
} from "./raceTapGate";
import { getCurrentObservedLatency } from "../../../hooks/useObservedLatency";

const FETCH_INITIAL_STATUS_COOLDOWN_MS = 1500;
// Keep our recent /today requests this long, for the burst guard.
const RECENT_SENDS_KEEP_MS = 3_000;
const STATUS_POLL_INTERVAL_MS = 15_000;
// Slow HTTP backstop kept running even while the WS is connected, so a
// missed/dropped `aotd.completed` frame can't leave the page stuck on
// "generating" forever.
const STATUS_POLL_SAFETY_INTERVAL_MS = 30_000;
const NO_ART_RETRY_DELAY_MS = 2000;
// WS frames that mean "the day moved" (someone tapped, a run started). A
// signed-out viewer can't race, so on these they quietly re-read /today to
// follow the day from the countdown into the painting.
const DAY_ACTIVITY_EVENTS = new Set([
  "participant:joined",
  "race:state",
  "state:changed",
  "aotd.progress.started",
  "aotd.progress.step",
]);

export interface UseAotdRaceQuote {
  input: string;
  set: (next: string) => void;
  submit: () => Promise<void>;
  suggest: () => Promise<void>;
  helpUsedCount: number;
  suggestionLoading: boolean;
}

export interface UseAotdRaceEmojis {
  next: string[];
}

export interface UseAotdRaceWayback {
  trigger: () => Promise<void>;
}

export interface UseAotdRaceResult {
  ui: UiState;
  retry: () => void;
  /**
   * Re-fire the `/today` race request without a full page reload. Honours the
   * same cooldown/rate-limit the backend burst limiter enforces
   * (`middleware/aotdRateLimit.ts`): if we're inside the local cooldown or the
   * server has handed back a `rate_limited` penalty, the call is a no-op and
   * `refreshBlocked` flips true so the UI can disable the button. The block
   * clears itself when the penalty elapses — there's no visible countdown.
   */
  refresh: () => void;
  /**
   * The race button. `tapStartedAt` is `performance.now()` when the finger
   * landed, for the tap-to-send telemetry. After unlock it is not held back by
   * a request that can't have entered the race (one that left before unlock,
   * or came back `not_started` / `entered_early`), so an early press never
   * swallows the real tap.
   */
  tap: (tapStartedAt?: number) => void;
  /** Server-synced clock for the unlock (see `useRaceClock`): arms the race button. */
  clock: RaceClock;
  /** True while a refresh is blocked by the cooldown / backend rate limit. */
  refreshBlocked: boolean;
  /**
   * True while a manual `/today` refresh request is actually in flight. The
   * page binds this to the refresh button's spinner so a press has immediate,
   * visible feedback instead of looking inert. Once unlock passes it drops for
   * a request that can't have entered the race, so the race button is live.
   */
  refreshing: boolean;
  /**
   * Refresh `/info` only (and the blob image when relevant). Use this for
   * background pollers (wayback) that need fresh art metadata without
   * tearing down `todayResponse` and flashing the page to `loading`.
   */
  refreshInfo: () => void;
  quote: UseAotdRaceQuote;
  emojis: UseAotdRaceEmojis;
  wayback: UseAotdRaceWayback;
  /** Exposed so `<PingFooter>` / debug surfaces can render WS status. */
  wsConnected: boolean;
  /**
   * The viewer's side of the no-emoji penalty (aotdrace-2.1): the warning
   * before they tap and what their tap got after. Undefined while loading or
   * when signed out.
   */
  emojiPenalty?: EmojiPenaltyResponse;
  /**
   * Time spent inside the CURRENT step, anchored at the latest WS step
   * heartbeat (`aotd.progress.step.elapsedMs`). Undefined when no live step
   * has arrived yet — the REST progress snapshot has no equivalent, so the
   * generating panel simply omits its elapsed indicator.
   */
  stepElapsedMs?: number;
}

/**
 * Orchestrator hook for the AOTD SPA. Combines:
 *   - the `/today` race hit (single source of generationStatus on first load),
 *   - the `/status` poller (HTTP fallback while generating),
 *   - the WebSocket race feed (live participant counts + completion),
 *   - the `/info` + `/today-image` refetches when generation finishes,
 *   - the blob-URL lifecycle,
 *   - the quote/emojis/wayback mutations the page exposes to its components.
 *
 * Returns one `UiState` for the page to switch on. All sub-state lives in
 * here — components never see raw API shapes.
 */
export function useAotdRace(): UseAotdRaceResult {
  const bootstrapStatus = useAppSelector((s) => s.auth.bootstrapStatus);
  const isAuthenticated = useAppSelector(
    (s) => s.auth.bootstrapStatus === "ready" && Boolean(s.auth.token),
  );

  // --- Race entry --------------------------------------------------------
  const [enterTodayRace, raceResult] = useEnterTodayRaceMutation();
  const [todayResponse, setTodayResponse] = useState<TodayResponse | null>(null);

  // Cooldown + rate-limit penalty (parity with `nextFetchAllowedAtRef`).
  const nextFetchAllowedAtRef = useRef<number>(0);
  const hasBootstrappedRef = useRef<boolean>(false);
  // No-art retry timer — only armed once per response.
  const noArtRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Refresh-button block (mirrors the backend burst limiter) ----------
  // Reactive flag the page binds to `disabled`. No countdown is rendered;
  // a single timer flips it back when the penalty elapses.
  const [refreshBlocked, setRefreshBlocked] = useState<boolean>(false);
  const refreshBlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armRefreshBlock = useCallback((until: number) => {
    if (refreshBlockTimerRef.current) {
      clearTimeout(refreshBlockTimerRef.current);
      refreshBlockTimerRef.current = null;
    }
    const ms = until - Date.now();
    if (ms <= 0) {
      setRefreshBlocked(false);
      return;
    }
    setRefreshBlocked(true);
    refreshBlockTimerRef.current = setTimeout(() => {
      setRefreshBlocked(false);
      refreshBlockTimerRef.current = null;
    }, ms);
  }, []);
  useEffect(() => {
    return () => {
      if (refreshBlockTimerRef.current) clearTimeout(refreshBlockTimerRef.current);
    };
  }, []);

  // Unlock instant of the day, when /today has told us (countdown, tap gating).
  const scheduledTimeRef = useRef<number | null>(null);
  // Server time at which the latest /today left, its answer (null while in
  // flight), and a sequence number so an older answer landing late can't
  // overwrite a newer one.
  const lastSentServerMsRef = useRef<number | null>(null);
  const lastVerdictRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);
  // Our recent /today requests (when they left, their round trip), for the burst guard.
  const recentSendsRef = useRef<SentRequest[]>([]);
  const heldSendRef = useRef(false);

  /** See `raceTapGate.requestCouldNotEnter`. */
  const requestCouldNotEnter = useCallback(
    (): boolean =>
      couldNotEnter({
        unlockMs: scheduledTimeRef.current,
        serverNowMs: raceServerNow(),
        sentServerMs: lastSentServerMsRef.current,
        verdict: lastVerdictRef.current,
      }),
    [],
  );

  const fireTodayRace = useCallback(async (
    tapPath: RaceTapPath = "auto",
    tapStartedAt?: number,
    bypassCooldown = false,
  ): Promise<void> => {
    if (!bypassCooldown && Date.now() < nextFetchAllowedAtRef.current) return;
    if (heldSendRef.current) return; // one send is already waiting out the burst limit
    // Never be the third /today inside the backend's burst window: waiting a
    // few hundred ms beats a 4.5s ban.
    const waitMs = burstWaitMs(recentSendsRef.current, Date.now());
    if (waitMs > 0) {
      heldSendRef.current = true;
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      heldSendRef.current = false;
    }
    const sentWallMs = Date.now();
    const sentRequest: SentRequest = { sentAtMs: sentWallMs, rttMs: null };
    recentSendsRef.current = [
      ...recentSendsRef.current.filter((r) => sentWallMs - r.sentAtMs < RECENT_SENDS_KEEP_MS),
      sentRequest,
    ];
    nextFetchAllowedAtRef.current = sentWallMs + FETCH_INITIAL_STATUS_COOLDOWN_MS;
    const seq = ++requestSeqRef.current;
    lastSentServerMsRef.current = raceServerNow();
    lastVerdictRef.current = null;
    const sentPerf = performance.now();
    try {
      const observedPingMs = getCurrentObservedLatency().pingMs;
      const result = await enterTodayRace({
        clientSentAt: new Date().toISOString(),
        pingMs: observedPingMs ?? undefined,
        tapPath,
        clockAheadMs: raceClockAheadMs(),
        tapDelayMs:
          typeof tapStartedAt === "number" ? performance.now() - tapStartedAt : undefined,
      }).unwrap();
      const rttMs = performance.now() - sentPerf;
      sentRequest.rttMs = rttMs;
      // Answers before unlock carry the server clock: the fallback sync for a
      // page opened in the last second, when no warm-up may go out.
      noteServerTime({
        serverNow: (result as TodayResponse & { serverNow?: number }).serverNow,
        sentWallMs,
        rttMs,
        fromToday: true,
      });
      if (seq !== requestSeqRef.current) return; // a newer tap owns the screen
      lastVerdictRef.current = result.generationStatus;
      setTodayResponse(result);
      // Rate-limit penalty: bump cooldown by retryAfterMs so the next click
      // can't immediately punch through.
      const retryAfterMs = (result as TodayResponse & { retryAfterMs?: number }).retryAfterMs;
      if (typeof retryAfterMs === "number" && result.generationStatus === "rate_limited") {
        nextFetchAllowedAtRef.current = Date.now() + retryAfterMs;
        armRefreshBlock(nextFetchAllowedAtRef.current);
        toast.warning(result.message || "Too many refreshes. Please wait.");
      }
    } catch (error) {
      console.error("AOTD: enterTodayRace failed", error);
      sentRequest.rttMs = performance.now() - sentPerf;
      if (seq !== requestSeqRef.current) return;
      lastVerdictRef.current = "error";
      setTodayResponse({
        generationStatus: "error",
        message: "Failed to connect to the server.",
      } as TodayResponse);
    }
  }, [enterTodayRace, armRefreshBlock]);

  // Fire once when auth bootstrap is ready.
  useEffect(() => {
    if (bootstrapStatus !== "ready") return;
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    void fireTodayRace();
  }, [bootstrapStatus, fireTodayRace]);

  // --- WS feed -----------------------------------------------------------
  const wsCompletedHandledRef = useRef<string | null>(null);
  // Read inside the socket callback (a stable closure), so kept in refs.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  const todayStatusRef = useRef<string | null>(null);
  todayStatusRef.current = todayResponse?.generationStatus ?? null;
  // `isAuthenticated` is passed as the socket's `authKey`: a fresh page load
  // often connects before auth bootstrap finishes (anonymous → no latency
  // sampling → raw-arrival race). Once auth is ready this reconnects the socket
  // with a token so the backend starts collecting ping samples in time.
  const { connected: wsConnected, state: wsState } = useAotdRaceSocket(true, (event) => {
    if (event.type === "aotd.completed") {
      // Mark so the effect below picks it up (force info+image refetch).
      wsCompletedHandledRef.current = event.artId ?? "completed";
      // Bridge completion into the authoritative response. The UI reducer only
      // advances generating → completed when `todayResponse.generationStatus`
      // flips to "completed"; the WS feed otherwise only updates `wsState`
      // (whose `generationStatus` is never read for the kind decision) and the
      // /status poller is throttled while WS is up — so without this the page
      // stayed stuck on "generating" after the art actually finished. This also
      // lets passive viewers (sitting on not_started/scheduled) auto-reveal the
      // finished art the moment the day's winner completes — no manual refresh.
      setTodayResponse((prev) =>
        prev && prev.generationStatus !== "completed"
          ? ({ ...prev, generationStatus: "completed" } as TodayResponse)
          : prev,
      );
      return;
    }
    // Signed-out viewers never tap (a tap is a race entry for members, so
    // members are never auto-fired). When the day moves while they sit on the
    // countdown or the "sign in to race" screen, re-read /today so they follow
    // it into the painting. fireTodayRace's cooldown absorbs bursts.
    if (
      !isAuthenticatedRef.current &&
      DAY_ACTIVITY_EVENTS.has(event.type) &&
      (todayStatusRef.current === "not_started" || todayStatusRef.current === "unauthorized")
    ) {
      void fireTodayRace();
    }
  }, isAuthenticated);

  // --- Status poller -----------------------------------------------------
  // Active while we're in a "generating" UiState (see derived `ui` below).
  // The WS feed is the primary completion signal, but we keep a slow HTTP
  // backstop running even while WS is connected (a dropped `aotd.completed`
  // frame must not strand the page on "generating"). When WS is down we poll
  // at the normal cadence.
  const [pollerActive, setPollerActive] = useState<boolean>(false);
  const statusQuery = useGetStatusQuery(undefined, {
    skip: !pollerActive,
    pollingInterval: pollerActive
      ? wsConnected
        ? STATUS_POLL_SAFETY_INTERVAL_MS
        : STATUS_POLL_INTERVAL_MS
      : 0,
  });

  // When /status pushes a new generationStatus, merge into our authoritative
  // response shape. This is the equivalent of `checkStatus` in the monolith.
  useEffect(() => {
    if (!statusQuery.data) return;
    setTodayResponse((prev) => mergeStatusIntoResponse(prev, statusQuery.data!));
  }, [statusQuery.data]);

  // --- Info + image refetches on completion -----------------------------
  // We need info+image in two cases:
  //   1. completed → render today's art card
  //   2. not_started + scheduledTime → backend returns previous AOTD as a
  //      preview during the shift (parity with monolith line 652-661).
  // Skip in all other states. UiState narrows below — but we need RTK Query
  // hooks at top level, so the skip flag is recomputed.
  const isCompleted =
    todayResponse?.generationStatus === "completed" ||
    wsCompletedHandledRef.current !== null;
  const isNotStartedWithSchedule =
    todayResponse?.generationStatus === "not_started" &&
    Boolean((todayResponse as TodayResponse & { scheduledTime?: string })?.scheduledTime);
  // The race clock runs while the day hasn't opened for this viewer yet, and on
  // the early-tap screen (its "tap again" arms by the same clock).
  const todayStatus = todayResponse?.generationStatus;
  const clockScheduledTime =
    todayStatus === "not_started" || todayStatus === "entered_early"
      ? ((todayResponse as TodayResponse & { scheduledTime?: string | null }).scheduledTime ?? null)
      : null;
  if (clockScheduledTime) {
    const parsed = Date.parse(clockScheduledTime);
    scheduledTimeRef.current = Number.isFinite(parsed) ? parsed : null;
  }
  const clock = useRaceClock(clockScheduledTime, Boolean(clockScheduledTime));

  const needsArtInfo = isCompleted || isNotStartedWithSchedule;
  // A member's page that hasn't loaded the previous art yet holds its /info
  // and image in the seconds around unlock. /info shares the backend's burst
  // limit with /today (page-load /today + /info + the tap inside 1s earned a
  // 4.5s ban), and the image is megabytes the tap would share the line with.
  // Once requested they are never held again, so nothing on screen blinks.
  const artRequestedRef = useRef(false);
  const artHeld =
    isAuthenticated &&
    isNotStartedWithSchedule &&
    !isCompleted &&
    !artRequestedRef.current &&
    inArtHoldWindow(clock.msToUnlock);
  const skipArt = !needsArtInfo || artHeld;
  if (!skipArt) artRequestedRef.current = true;
  const infoQuery = useGetInfoQuery(undefined, { skip: skipArt });
  const imageQuery = useGetTodayImageQuery(undefined, { skip: skipArt });

  // When WS says completed, force an info+image refetch (parity with monolith).
  useEffect(() => {
    if (!wsCompletedHandledRef.current) return;
    if (infoQuery.isUninitialized || imageQuery.isUninitialized) return;
    void infoQuery.refetch();
    void imageQuery.refetch();
    wsCompletedHandledRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsCompletedHandledRef.current, infoQuery.isUninitialized, imageQuery.isUninitialized]);

  // --- Blob URL lifecycle ------------------------------------------------
  const artUrl = imageQuery.data ?? null;
  const prevArtUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevArtUrlRef.current;
    if (prev && prev !== artUrl && prev.startsWith("blob:")) {
      // Defer the revoke so React has flushed any consumer that's still
      // pointing at the old URL during this render.
      const id = setTimeout(() => URL.revokeObjectURL(prev), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [artUrl]);
  useEffect(() => {
    prevArtUrlRef.current = artUrl;
  }, [artUrl]);
  // Final unmount sweep.
  useEffect(() => {
    return () => {
      const url = prevArtUrlRef.current;
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    };
  }, []);

  // --- Derived UiState ---------------------------------------------------
  // For not_started + completed, the context needs `previousArt` / live art
  // from the info query — merge info into the response when available so the
  // reducer sees the populated ArtInfo.
  const responseForReducer = useMemo<TodayResponse | StatusResponse | null>(() => {
    if (!todayResponse) return null;
    if (todayResponse.generationStatus === "completed" && infoQuery.data) {
      return { ...infoQuery.data, ...todayResponse, ...infoQuery.data } as TodayResponse;
    }
    if (todayResponse.generationStatus === "not_started" && infoQuery.data) {
      return { ...todayResponse, ...infoQuery.data, generationStatus: "not_started" } as TodayResponse;
    }
    return todayResponse;
  }, [todayResponse, infoQuery.data]);

  const ui: UiState = useMemo(() => {
    if (bootstrapStatus !== "ready") {
      return { kind: "connecting" };
    }
    if (raceResult.isLoading && !todayResponse) {
      // While the very first /today is in flight, prefer the softer
      // "connecting" label until WS is up — that way we don't flash
      // through "loading" → "initializing" → "warming up" in rapid succession.
      return wsConnected ? { kind: "loading" } : { kind: "connecting" };
    }
    if (!todayResponse) {
      return { kind: "loading" };
    }
    // Hold the loading screen during the initial completed-state hydration —
    // info + image are required before we render the art card, otherwise the
    // user briefly sees an empty card. Parity with monolith `setIsLoading(false)`
    // only after Promise.all([fetchArtInfo, fetchArtImage]).
    if (todayResponse.generationStatus === "completed") {
      if (!infoQuery.data || !imageQuery.data) {
        return { kind: "loading" };
      }
    }
    const reduced = reduceTodayResponse(responseForReducer, {
      isAuthenticated,
      previousArt: infoQuery.data ?? undefined,
      artUrl,
    });
    if (reduced.kind === "generating") {
      return {
        ...reduced,
        // Prefer the reducer's counts (from /status, which carries emojis) but
        // fall back to the live WS feed. This matters for race losers: their
        // race_lost body carries no counts, so the WS `participant:joined`
        // count is what fills the "N users think they got the aotd" suspense
        // strip before the first /status poll lands.
        // Racers only ever join, so the larger count is the fresher one.
        participantCount: maxDefined(reduced.participantCount, wsState.participantCount),
        participantEmojis: reduced.participantEmojis ?? wsState.participantEmojis,
        // The WS strip is live (a frame per new racer); /status only lands
        // every 15–30s, so it's the fallback.
        racers: pickRacers(wsState.racers, reduced.racers),
        // WS step fields stay primary; the reducer's values (hydrated from the
        // persisted /today `progress` snapshot) only fill the gap for a page
        // opened mid-generation before the first WS tick lands.
        stepLabel: wsState.label ?? reduced.stepLabel,
        stepName: wsState.stepName ?? reduced.stepName,
        stepIndex: typeof wsState.stepIndex === "number" ? wsState.stepIndex : reduced.stepIndex,
        totalSteps: typeof wsState.totalSteps === "number" ? wsState.totalSteps : reduced.totalSteps,
        percent: typeof wsState.percent === "number" ? wsState.percent : reduced.percent,
        // Live partial-image preview frames arrive only over the WS feed.
        previewB64: wsState.previewB64,
        previewIndex: wsState.previewIndex,
      };
    }
    return reduced;
  }, [
    bootstrapStatus,
    raceResult.isLoading,
    todayResponse,
    responseForReducer,
    isAuthenticated,
    infoQuery.data,
    imageQuery.data,
    artUrl,
    wsConnected,
    wsState.participantCount,
    wsState.participantEmojis,
    wsState.racers,
    wsState.label,
    wsState.stepName,
    wsState.stepIndex,
    wsState.totalSteps,
    wsState.percent,
    wsState.previewB64,
    wsState.previewIndex,
  ]);

  // Activate/deactivate the poller from the reduced UI state.
  useEffect(() => {
    const shouldPoll = ui.kind === "generating";
    setPollerActive((prev) => (prev !== shouldPoll ? shouldPoll : prev));
  }, [ui.kind]);

  // --- Auto-retry on no_art (only when authenticated). Parity with monolith
  // lines 734–748: kick a second /today to nudge the backend, then re-check.
  useEffect(() => {
    if (noArtRetryTimerRef.current) {
      clearTimeout(noArtRetryTimerRef.current);
      noArtRetryTimerRef.current = null;
    }
    if (ui.kind !== "no_art_or_unauthorized") return;
    if (!ui.isAuthenticated) return;
    noArtRetryTimerRef.current = setTimeout(() => {
      void fireTodayRace();
    }, NO_ART_RETRY_DELAY_MS);
    return () => {
      if (noArtRetryTimerRef.current) {
        clearTimeout(noArtRetryTimerRef.current);
        noArtRetryTimerRef.current = null;
      }
    };
  }, [ui, fireTodayRace]);

  // --- Quote + emojis + wayback mutations -------------------------------
  const [quoteInput, setQuoteInput] = useState<string>("");
  const [quoteHelpUsed, setQuoteHelpUsed] = useState<number>(0);
  const [suggestionLoading, setSuggestionLoading] = useState<boolean>(false);

  // Sync quote draft from /info when the server has a non-empty value.
  useEffect(() => {
    const serverQuote = infoQuery.data?.authorQuote;
    if (typeof serverQuote === "string" && serverQuote.length > 0) {
      setQuoteInput(serverQuote);
    }
    if (typeof infoQuery.data?.quoteHelp === "number") {
      setQuoteHelpUsed(infoQuery.data.quoteHelp);
    }
  }, [infoQuery.data?.authorQuote, infoQuery.data?.quoteHelp]);

  const [setQuoteMutation] = useSetQuoteMutation();
  const [suggestQuoteMutation] = useSuggestQuoteMutation();
  const [triggerWaybackMutation] = useTriggerWaybackMutation();

  const submitQuote = useCallback(async () => {
    try {
      await setQuoteMutation({ quote: quoteInput }).unwrap();
      toast.success("Quote submitted successfully!");
    } catch (err: any) {
      const msg = err?.data?.message || "Failed to submit quote.";
      toast.error(msg);
    }
  }, [setQuoteMutation, quoteInput]);

  const suggestQuote = useCallback(async () => {
    if (quoteHelpUsed >= 3) return;
    setSuggestionLoading(true);
    try {
      const resp = (await suggestQuoteMutation({ quote: "" }).unwrap()) as {
        suggestedQuote?: string;
        quoteHelp?: number;
      };
      if (resp?.suggestedQuote) setQuoteInput(resp.suggestedQuote);
      if (typeof resp?.quoteHelp === "number") setQuoteHelpUsed(resp.quoteHelp);
      toast.info("AI suggested a quote.");
    } catch (err: any) {
      const msg = err?.data?.message || "Failed to get suggestion.";
      toast.error(msg);
    } finally {
      setSuggestionLoading(false);
    }
  }, [suggestQuoteMutation, quoteHelpUsed]);

  const triggerWaybackCb = useCallback(async () => {
    try {
      await triggerWaybackMutation().unwrap();
      toast.success("Wayback capture triggered");
    } catch (err: any) {
      const msg = err?.data?.message || "Failed to trigger wayback capture.";
      toast.error(msg);
    }
  }, [triggerWaybackMutation]);

  // --- Public retry handler (rate-limit retry, error retry, etc.) -------
  const retry = useCallback(() => {
    nextFetchAllowedAtRef.current = 0;
    setTodayResponse(null);
    // Retry, generate and check-again are presses; the birthday screen also
    // calls this by itself when the hold lifts. Tell them apart by the
    // browser's user activation where it has one.
    const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } })
      .userActivation;
    void fireTodayRace(activation && !activation.isActive ? "auto" : "refresh");
  }, [fireTodayRace]);

  // Background-poller friendly refresh — does NOT reset `todayResponse`,
  // so the UI stays on the current state instead of flashing to `loading`.
  // Used by `useWaybackPolling` to pick up wayback-capture progress while
  // sitting on the completed art card.
  const refreshInfo = useCallback(() => {
    if (!infoQuery.isUninitialized) void infoQuery.refetch();
  }, [infoQuery]);

  // Manual refresh button — re-fires `/today` (sends the request, no reload)
  // and always gives the user *some* visible feedback, because the most common
  // case (re-checking during generation) returns the same UiState and used to
  // look completely inert. Three outcomes:
  //   1. A request is already in flight → no-op; the button's spinner
  //      (`refreshing`) is already communicating, and re-firing would risk a
  //      second concurrent /today race loop for the same user.
  //   2. Inside the cooldown / rate-limit penalty → surface the block + a
  //      "too fast" toast, matching the backend burst limiter.
  //   3. Otherwise → fire and toast "Refreshing…" so the press registers even
  //      when the resulting state is unchanged.
  const refresh = useCallback(() => {
    // A request that can't have entered the race must not hold back (or cool
    // down) the first press after unlock.
    const afterEarlyRequest = requestCouldNotEnter();
    if (raceResult.isLoading && !afterEarlyRequest) return;
    const now = Date.now();
    if (now < nextFetchAllowedAtRef.current && !afterEarlyRequest) {
      armRefreshBlock(nextFetchAllowedAtRef.current);
      toast.info("Hang tight — refreshing a touch too fast.");
      return;
    }
    toast.info("Refreshing…", { autoClose: 1500 });
    void fireTodayRace("refresh", undefined, afterEarlyRequest);
  }, [raceResult.isLoading, fireTodayRace, armRefreshBlock, requestCouldNotEnter]);

  const tap = useCallback(
    (tapStartedAt?: number) => {
      const afterEarlyRequest = requestCouldNotEnter();
      if (raceResult.isLoading && !afterEarlyRequest) return;
      if (!afterEarlyRequest && Date.now() < nextFetchAllowedAtRef.current) {
        toast.info("Hang tight, your last tap is still being checked.");
        return;
      }
      void fireTodayRace("button", tapStartedAt, afterEarlyRequest);
    },
    [raceResult.isLoading, fireTodayRace, requestCouldNotEnter],
  );

  // The viewer's no-emoji penalty. Re-read whenever the day's kind changes
  // (countdown → painting → done) and after every /today (tag invalidation),
  // so the waiting screen can tell a racer what their tap got.
  const emojiPenaltyQuery = useGetEmojiPenaltyQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { refetch: refetchEmojiPenalty } = emojiPenaltyQuery;
  const emojiPenaltyUninitialized = emojiPenaltyQuery.isUninitialized;
  useEffect(() => {
    if (emojiPenaltyUninitialized) return;
    void refetchEmojiPenalty();
  }, [ui.kind, emojiPenaltyUninitialized, refetchEmojiPenalty]);

  const nextEmojis = useMemo<string[]>(
    () => infoQuery.data?.nextAOTDEmojis ?? [],
    [infoQuery.data?.nextAOTDEmojis],
  );

  return {
    ui,
    retry,
    refresh,
    tap,
    clock,
    refreshBlocked,
    // Read every render; the clock's repaint at unlock re-runs it.
    refreshing: raceResult.isLoading && !requestCouldNotEnter(),
    refreshInfo,
    quote: {
      input: quoteInput,
      set: setQuoteInput,
      submit: submitQuote,
      suggest: suggestQuote,
      helpUsedCount: quoteHelpUsed,
      suggestionLoading,
    },
    emojis: {
      next: nextEmojis,
    },
    wayback: {
      trigger: triggerWaybackCb,
    },
    wsConnected,
    emojiPenalty: isAuthenticated ? emojiPenaltyQuery.data : undefined,
    stepElapsedMs: wsState.elapsedMs,
  };
}

function maxDefined(a?: number, b?: number): number | undefined {
  if (typeof a === "number" && typeof b === "number") return Math.max(a, b);
  return typeof a === "number" ? a : b;
}

function pickRacers(
  live?: WaitingRoomRacer[],
  polled?: WaitingRoomRacer[],
): WaitingRoomRacer[] | undefined {
  if (Array.isArray(live) && live.length > 0) return live;
  return Array.isArray(polled) ? polled : live;
}

/**
 * Merge a `/status` payload into the prior `/today` response without
 * stomping the original keys that aren't part of /status (scheduledTime,
 * etc.). When /status flips to a terminal status, the new generationStatus
 * wins.
 */
function mergeStatusIntoResponse(
  prev: TodayResponse | null,
  next: StatusResponse,
): TodayResponse {
  const base: Partial<TodayResponse> = prev ?? {};
  return {
    ...base,
    ...next,
    generationStatus: next.generationStatus,
  } as TodayResponse;
}

export type { ArtInfo };
